'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer'
import type { PaginatedMeta, SalaryType } from '@/lib/generated/graphql-types'
import { getSalaryDetails } from '@/modules/salary/common/salary-details-utils'
import {
  formatYearsOfExperience,
  calculateTotalNumericCompensation,
  isHourly,
} from '@/modules/salary/common/utils'
import { SalaryDetailsFooter } from '@/modules/salary/details/details-footer'
import { OverviewBenefits } from '@/modules/salary/details/overview-benefits'
import { TotalCompDetails } from '@/modules/salary/details/total-comp'
import { useIsMobile } from '@/utils/screen'
import { ArrowLeft, X } from 'lucide-react'
import type { SalaryDetails as SalaryDetailsType } from '@/modules/salary/types'
import { EditReviewLink } from '@/modules/salary/details/edit-review-link'
import {
  type OnboardingAccountType,
  OnboardingPageIds,
} from '@/modules/surveys/configs/onboarding/constants'
import { useSalaryFilters } from '@/modules/salary/filters/use-salary-filters'
import { DetailNote } from '@/modules/salary/details/detail-note'
import { useSalaryDetails } from './use-salary-details'
import type { SalaryPayPeriod } from '@/modules/salary/table/constants'
import { usePostHog } from 'posthog-js/react'
import { useProfessionField } from '@/modules/onboarding/use-profession-field'
import { useEffect, useState, useCallback } from 'react'
import { SalaryFilterParam } from '../constants'
import { useSalaryTableStore } from '@/modules/salary/salary-table-context'
import { usePathname, useSearchParams } from 'next/navigation'
import { Badge } from '@/components/ui/badge'
import { useRemoveSearchParams } from '@/hooks/use-remove-search-params'
import {
  LOCUM_TENENS_VALUE,
  PRN_VALUE,
  SALARY_LOCUM_TENENS_VALUE,
} from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import { MainRoutes } from '@/utils/routes'

export function SalaryDetailsContent({
  salaryDetails,
  isOnboardingReviewPane,
  accountType,
  isAuthenticated,
  resultRank,
  showHourly,
  isLocumsOrPrn,
}: {
  salaryDetails: SalaryDetailsType
  isOnboardingReviewPane?: boolean
  accountType?: OnboardingAccountType
  isAuthenticated: boolean
  resultRank?: number | null
  showHourly?: boolean
  isLocumsOrPrn: boolean
}) {
  const posthog = usePostHog()
  const searchParams = useSearchParams()
  const searchId = useSalaryTableStore((state) => state.searchId)
  const pageId = searchParams.get(SalaryFilterParam.page) ?? '1'
  const pathname = usePathname()
  const isAddSalary = pathname.startsWith(MainRoutes.ProfileSalariesAdd)
  const isEditSalary = pathname.startsWith(MainRoutes.ProfileSalariesEdit)

  useEffect(() => {
    posthog.capture('viewed_salary_result', {
      screen_name: 'salary_details',
      profession_name: salaryDetails.professionTitle,
      specialty_name: salaryDetails.specialtyTitle,
      location_name: salaryDetails.location,
      page_id: pageId,
      salary_search_id: searchId,
      salary_id: salaryDetails.salaryId,
      result_rank: resultRank ?? undefined,
      total_comp: calculateTotalNumericCompensation(salaryDetails),
    })
  }, [searchId, resultRank, salaryDetails, posthog, pageId])

  const { professionField } = useProfessionField()

  return (
    <div className="rounded-lg bg-background-primary sm:bg-transparent">
      <div className="px-4 flex flex-col">
        <div className="flex justify-between">
          <h2 className="text-lg text-copy-primary font-bold pt-4 sm:pt-0">
            {salaryDetails.specialtyTitle}
          </h2>
          {isOnboardingReviewPane && !isAddSalary && !isEditSalary ? (
            <div className="sm:pt-0 pt-4">
              <EditReviewLink
                pageId={OnboardingPageIds.PersonalInfoProvider}
                onClick={() => {
                  posthog.capture('clicked_about_job_edit', {
                    screen_name: 'review_salary',
                    product_area: 'onboarding',
                    module_name: 'main',
                    profession_type: accountType,
                    profession: professionField,
                  })
                }}
              />
            </div>
          ) : null}
        </div>
        <div className="flex text-sm flex-wrap text-copy-primary">
          <span className="pr-2">{salaryDetails.location}</span>
          <span className="hidden lg:inline pr-2"> · </span>
          <span>
            {formatYearsOfExperience(salaryDetails.yearsOfExperience)}
          </span>
        </div>
      </div>

      <TotalCompDetails
        salaryDetails={salaryDetails}
        isOnboardingReviewPane={isOnboardingReviewPane}
        accountType={accountType}
        isAuthenticated={isAuthenticated}
        showHourly={showHourly}
        isLocumsOrPrn={isLocumsOrPrn}
      />
      <OverviewBenefits
        salaryDetails={salaryDetails}
        isOnboardingReviewPane={isOnboardingReviewPane}
        accountType={accountType}
      />
      <div className="w-full h-px bg-background-muted" />
      {salaryDetails.otherNoteworthyComp && !isLocumsOrPrn ? (
        <DetailNote
          title={`${salaryDetails.professionTitle} Note`}
          className="my-4"
        >
          {salaryDetails.otherNoteworthyComp}
        </DetailNote>
      ) : null}
    </div>
  )
}

interface SalaryDetailsContentProps {
  salaryDetails: SalaryDetailsType
  salary: SalaryType
  onClose: () => void
  salaries: SalaryType[]
  meta?: PaginatedMeta
  isAuthenticated: boolean
  countPublicInteraction: () => void
}

function SalaryDetailsCard({
  onClose,
  salaryDetails,
  salary,
  salaries,
  meta,
  isAuthenticated,
  countPublicInteraction,
}: SalaryDetailsContentProps) {
  const { salaryFilterControls } = useSalaryFilters({
    isAuthenticated,
  })

  const searchParams = useSearchParams()
  const payPeriod = searchParams.get(SalaryFilterParam.payPeriod)
  const jobType = salaryFilterControls?.jobType
  const showHourly = isHourly(jobType, payPeriod as SalaryPayPeriod | null)
  const isLocumsOrPrn =
    jobType === PRN_VALUE ||
    jobType === LOCUM_TENENS_VALUE ||
    jobType === SALARY_LOCUM_TENENS_VALUE
  const pageOffset = ((meta?.pageNumber ?? 1) - 1) * (meta?.pageSize ?? 0)
  const resultRank =
    salaries.findIndex((s) => s.salaryId === salary.salaryId) + 1 + pageOffset

  const [startTime] = useState(Date.now())
  const posthog = usePostHog()
  const trackDetailEvent = useCallback(
    (action: 'share' | 'report' | 'like') => {
      const elapsedTime = Math.floor((Date.now() - startTime) / 1000) // Convert to seconds
      posthog.capture('clicked_on_salary_detail', {
        screen_name: 'salary_details',
        profession_name: salaryDetails.professionTitle,
        specialty_sub: salaryDetails.specialtyTitle,
        location: salaryDetails.location,
        salary_id: salaryDetails.salaryId,
        clicked_share: action === 'share',
        clicked_report: action === 'report',
        clicked_like: action === 'like',
        elapsed_time: elapsedTime,
      })
    },
    [posthog, salaryDetails, startTime],
  )

  return (
    <Card
      className="w-full h-full px-0 overflow-y-hidden flex flex-col max-h-[1000px]"
      data-testid="salary-details-pane"
    >
      <CardHeader
        className={`border-b border-background-muted py-2 pr-4 pl-0 sm:pl-4 flex-row justify-between 
      items-center sm:px-6 px-2`}
      >
        <div className="flex gap-4 items-center ml-0">
          <Button
            className="block sm:hidden py-0 px-2"
            onClick={onClose}
            variant="ghost"
            aria-label="Go back from salary details pane"
          >
            <ArrowLeft />
          </Button>
          <p className="text-copy-muted text-sm">{salaryDetails.postedOn}</p>
          {salaryDetails.submittedByCurrentUser ? (
            <Badge variant="yellow">You</Badge>
          ) : null}
        </div>
        <Button
          onClick={() => onClose()}
          className="p-0.5 mt-0 hidden sm:block h-fit"
          variant="ghost"
          aria-label="Close salary details pane"
        >
          <X className="w-6 h-6 stroke-copy-accent" />
        </Button>
      </CardHeader>
      {salary ? (
        <CardContent className="p-0 sm:px-2 space-y-4 mt-2 overflow-y-auto grow pb-4">
          <SalaryDetailsContent
            salaryDetails={salaryDetails}
            showHourly={showHourly}
            isLocumsOrPrn={isLocumsOrPrn}
            isAuthenticated={isAuthenticated}
            resultRank={resultRank}
          />
        </CardContent>
      ) : null}
      <CardFooter
        className="flex justify-between py-4 sm:px-6 px-2 \
      border-t border-border-muted"
      >
        <SalaryDetailsFooter
          salary={salary}
          salaries={salaries}
          meta={meta}
          isAuthenticated={isAuthenticated}
          onTrackEvent={trackDetailEvent}
          countPublicInteraction={countPublicInteraction}
        />
      </CardFooter>
    </Card>
  )
}

interface SalaryDetailsProps {
  salaries: SalaryType[]
  salaryMeta?: PaginatedMeta
  isOpen: boolean
  isAuthenticated: boolean
  countPublicInteraction: () => void
}

export function SalaryDetails({
  salaries,
  isOpen,
  salaryMeta,
  isAuthenticated,
  countPublicInteraction,
}: SalaryDetailsProps) {
  const isMobile = useIsMobile()

  const { salary } = useSalaryDetails({ salaries })
  const salaryDetails = getSalaryDetails(salary)
  const removeSearchParams = useRemoveSearchParams()

  if (!salary) {
    return null
  }

  const onClose = () => {
    removeSearchParams(SalaryFilterParam.salaryId, { pushState: true })
  }

  if (!isMobile && isOpen) {
    return (
      <SalaryDetailsCard
        onClose={onClose}
        salaryDetails={salaryDetails}
        salary={salary}
        salaries={salaries}
        meta={salaryMeta}
        isAuthenticated={isAuthenticated}
        countPublicInteraction={countPublicInteraction}
      />
    )
  }

  return (
    <Drawer
      snapPoints={[0, 1]}
      fadeFromIndex={0}
      direction="right"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      activeSnapPoint={1}
      setActiveSnapPoint={(snapPoint) => {
        if (snapPoint === 0) {
          onClose()
        }
      }}
      autoFocus
    >
      <DrawerContent className="bg-background-primary border-background-muted h-full w-full">
        <DrawerTitle className="sr-only">Salary details</DrawerTitle>
        <SalaryDetailsCard
          onClose={onClose}
          salaryDetails={salaryDetails}
          salary={salary}
          salaries={salaries}
          meta={salaryMeta}
          isAuthenticated={isAuthenticated}
          countPublicInteraction={countPublicInteraction}
        />
      </DrawerContent>
    </Drawer>
  )
}
